import {
    CommunityId,
    ContactId,
    genEntityId,
    HomeCareLeadId,
    PlacementId,
    RegionId,
} from "@sp-crm/core";
import { SupportEmailLink } from "components/app/support-email-link";
import { PowerOfAttorneySelect } from "components/clients/show-client/power-of-attorney-select";
import { Header } from "components/header";
import { LocaleContext } from "components/locale-provider/locale-provider";
import { Setting } from "components/setting";
import { PhoneInputE164 } from "components/shared/phone-input-e164";
import { AutosavingCheckbox } from "components/ui/autosaving-checkbox";
import { AutosavingInput } from "components/ui/autosaving-input";
import { AutosavingStateInput } from "components/ui/autosaving-state-input";
import { InlineBanner } from "components/ui/inline-banner";
import { PrimaryButton } from "components/ui/primary-button";
import { Spinner } from "components/ui/spinner";
import {
    AdvancedSearchEntityType,
    ClientContactRelationship,
    ContactWithLinksFragment,
    HomeCareLeadContactRelationship,
    PatchAttachedReferenceContactInput,
    PatchContactInput,
    useCreateContactMutation,
    useGetContactQuery,
    usePatchContactMutation,
} from "generated/graphql";
import { produce } from "immer";
import React, { useCallback, useEffect } from "react";
import { useRegionId } from "store/selectors/hooks";
import { ContactEditorEntity, getEditorPolicy } from "./editor-policy";

interface ContactEditorProps {
    initialContactId: ContactId;
    entity: ContactEditorEntity;
    onSaved: (contact: ContactWithLinksFragment) => void;
    onClose: () => void;
}

type FieldUpdate =
    | {
          type: "contact";
          patch: Partial<PatchContactInput>;
      }
    | {
          type: "communityContact";
          communityId: CommunityId;
          patch: Partial<PatchContactInput["communityContacts"][0]>;
      }
    | {
          type: "referralContact";
          patch: Partial<PatchAttachedReferenceContactInput>;
      }
    | {
          type: "clientContact";
          clientId: PlacementId;
          relationship: ClientContactRelationship;
          patch: Partial<PatchContactInput["clientContacts"][0]>;
      }
    | {
          type: "homeCareLeadContact";
          homeCareLeadId: HomeCareLeadId;
          relationship: HomeCareLeadContactRelationship;
          patch: Partial<PatchContactInput["homeCareLeadContacts"][0]>;
      };

interface ComponentState {
    status: "loading" | "saving" | "loaded" | "failed";
    contact: ContactWithLinksFragment | null;
    pendingUpdates: FieldUpdate[];
    hasSaved: boolean;
    contactExists: boolean;
    closeRequested: boolean;
    errorMessage: string;
}

const defaultErrorMessage = "The contact failed to save.";

type Action =
    | { type: "setContact"; contact: ContactWithLinksFragment }
    | { type: "enqueueUpdate"; fieldUpdates: FieldUpdate[] }
    | { type: "startPatch" }
    | { type: "startCreate" }
    | { type: "ensureReferralContact"; regionId: RegionId }
    | { type: "clearReferralContacts" }
    | { type: "requestClose" }
    | { type: "setError"; message: string };

const reducer: React.Reducer<ComponentState, Action> = (p, a): ComponentState => {
    return produce(p, draft => {
        switch (a.type) {
            case "setContact": {
                draft.contact = a.contact;
                draft.status = "loaded";
                draft.contactExists = true;
                break;
            }
            case "enqueueUpdate": {
                draft.pendingUpdates.push(...a.fieldUpdates);
                break;
            }
            case "startPatch": {
                draft.status = "saving";
                draft.pendingUpdates = [];
                draft.hasSaved = true;
                break;
            }
            case "startCreate": {
                draft.status = "saving";
                draft.hasSaved = true;
                break;
            }
            case "ensureReferralContact": {
                if (draft.contact) {
                    draft.contact.referenceContacts.push(
                        emptyReferenceContact(a.regionId),
                    );
                }
                break;
            }
            case "clearReferralContacts": {
                if (draft.contact) {
                    draft.contact.referenceContacts = [];
                }
                break;
            }
            case "requestClose": {
                draft.closeRequested = true;
                break;
            }
            case "setError": {
                draft.status = "failed";
                draft.errorMessage = a.message || defaultErrorMessage;
                break;
            }
        }
    });
};

const emptyReferenceContact = (
    regionId: RegionId,
): ContactWithLinksFragment["referenceContacts"][0] => {
    return {
        dateAdded: new Date().toISOString(),
        createdBy: null,
        id: genEntityId(),
        updatedAt: new Date().toISOString(),
        updatedBy: null,
        regionId,
        appLink: "",
    };
};

const emptyContact = (
    entity: ContactEditorEntity,
    regionId: RegionId,
): ContactWithLinksFragment => {
    const id = genEntityId<ContactId>();

    const communityContacts: ContactWithLinksFragment["communityContacts"] =
        entity.type === AdvancedSearchEntityType.Community
            ? [
                  {
                      communityId: entity.communityId,
                      contactId: id,
                      primary: true,
                      community: { id: entity.communityId, appLink: "" },
                  },
              ]
            : [];

    const referenceContacts: ContactWithLinksFragment["referenceContacts"] =
        entity.type === AdvancedSearchEntityType.Community
            ? [emptyReferenceContact(regionId)]
            : [];

    const clientContacts: ContactWithLinksFragment["clientContacts"] =
        entity.type === AdvancedSearchEntityType.Client
            ? [
                  {
                      placementId: entity.clientId,
                      contactId: id,
                      relationship: entity.relationship,
                      powerOfAttorney: [],
                      placement: { id: entity.clientId, appLink: "" },
                  },
              ]
            : [];

    const homeCareLeadContacts: ContactWithLinksFragment["homeCareLeadContacts"] =
        entity.type === AdvancedSearchEntityType.HomeCareLead
            ? [
                  {
                      homeCareLeadId: entity.homeCareLeadId,
                      contactId: id,
                      relationship: entity.relationship,
                      powerOfAttorney: [],
                      homeCareLead: { id: entity.homeCareLeadId, appLink: "" },
                  },
              ]
            : [];

    return {
        id,
        email1OptOut: false,
        clientContacts,
        communityContacts,
        homeCareLeadContacts,
        referenceContacts,
    };
};

const initialState = (props: ContactEditorProps, regionId: RegionId): ComponentState => {
    return {
        status: props.initialContactId ? "loading" : "loaded",
        contact: props.initialContactId ? null : emptyContact(props.entity, regionId),
        pendingUpdates: [],
        contactExists: false,
        hasSaved: false,
        closeRequested: false,
        errorMessage: "",
    };
};

const matchingCommunityContactFor = (
    contact: ContactWithLinksFragment | null,
    entity: ContactEditorEntity,
): ContactWithLinksFragment["communityContacts"][0] | null => {
    if (!contact || entity.type !== AdvancedSearchEntityType.Community) return null;
    return (
        contact.communityContacts.find(cc => cc.communityId === entity.communityId) ||
        null
    );
};

const matchingClientContactFor = (
    contact: ContactWithLinksFragment | null,
    entity: ContactEditorEntity,
): ContactWithLinksFragment["clientContacts"][0] | null => {
    if (!contact || entity.type !== AdvancedSearchEntityType.Client) return null;
    return (
        contact.clientContacts.find(
            cc =>
                cc.placementId === entity.clientId &&
                cc.relationship === entity.relationship,
        ) || null
    );
};

const matchingHomeCareLeadContactFor = (
    contact: ContactWithLinksFragment | null,
    entity: ContactEditorEntity,
): ContactWithLinksFragment["homeCareLeadContacts"][0] | null => {
    if (!contact || entity.type !== AdvancedSearchEntityType.HomeCareLead) return null;
    return (
        contact.homeCareLeadContacts?.find(
            hcc =>
                hcc.homeCareLeadId === entity.homeCareLeadId &&
                hcc.relationship === entity.relationship,
        ) || null
    );
};

export const ContactEditor: React.FC<ContactEditorProps> = props => {
    const { initialContactId, entity, onSaved, onClose } = props;
    const regionId = useRegionId();

    const [state, dispatch] = React.useReducer(reducer, initialState(props, regionId));

    const contactQuery = useGetContactQuery(
        { contactId: initialContactId },
        { enabled: false },
    );

    const createContact = useCreateContactMutation();
    const patchContact = usePatchContactMutation();

    useEffect(() => {
        if (initialContactId) {
            contactQuery.refetch();
        }
    }, [initialContactId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (contactQuery.data?.getContact) {
            dispatch({ type: "setContact", contact: contactQuery.data.getContact });
        }
    }, [contactQuery.data?.getContact]);

    const doPatch = useCallback(
        async (contactId: ContactId, updatesToMake: FieldUpdate[]) => {
            dispatch({ type: "startPatch" });

            const params: PatchContactInput = updatesToMake.reduce(
                (agg: PatchContactInput, updateToMake) => {
                    switch (updateToMake.type) {
                        case "contact":
                            return {
                                ...agg,
                                ...updateToMake.patch,
                            };
                        case "communityContact":
                            return {
                                ...agg,
                                communityContacts: [
                                    ...(agg.communityContacts || []),
                                    {
                                        communityId: updateToMake.communityId,
                                        ...updateToMake.patch,
                                    },
                                ],
                            };
                        case "clientContact":
                            return {
                                ...agg,
                                clientContacts: [
                                    ...(agg.clientContacts || []),
                                    {
                                        clientId: updateToMake.clientId,
                                        relationship: updateToMake.relationship,
                                        ...updateToMake.patch,
                                    },
                                ],
                            };
                        case "homeCareLeadContact":
                            return {
                                ...agg,
                                homeCareLeadContacts: [
                                    ...(agg.homeCareLeadContacts || []),
                                    {
                                        homeCareLeadId: updateToMake.homeCareLeadId,
                                        relationship: updateToMake.relationship,
                                        ...updateToMake.patch,
                                    },
                                ],
                            };
                        case "referralContact":
                            return {
                                ...agg,
                                referenceContacts: [
                                    ...(agg.referenceContacts || []),
                                    {
                                        regionId,
                                        ...updateToMake.patch,
                                    },
                                ],
                            };
                    }
                },
                { id: contactId },
            );
            try {
                const patched = await patchContact.mutateAsync({ params });
                dispatch({ type: "setContact", contact: patched.patchContact });
            } catch (e) {
                const message = e?.message || defaultErrorMessage;
                dispatch({ type: "setError", message });
            }
        },
        [patchContact, dispatch, regionId],
    );

    const handleFieldUpdates = useCallback(
        async (fieldUpdates: FieldUpdate[]) => {
            if (state.status === "saving") {
                dispatch({ type: "enqueueUpdate", fieldUpdates });
            } else if (state.status === "loaded") {
                if (state.contactExists) {
                    const updatesToMake = [...state.pendingUpdates, ...fieldUpdates];
                    await doPatch(state.contact.id, updatesToMake);
                } else {
                    dispatch({ type: "startCreate" });
                    dispatch({ type: "enqueueUpdate", fieldUpdates });

                    try {
                        const created = await createContact.mutateAsync({
                            params: {
                                linkedEntityId:
                                    entity.type === AdvancedSearchEntityType.Client
                                        ? entity.clientId
                                        : entity.type ===
                                            AdvancedSearchEntityType.HomeCareLead
                                          ? entity.homeCareLeadId
                                          : entity.communityId,
                                linkedEntityType: entity.type,
                                referenceContactParams:
                                    state.contact.referenceContacts.length > 0 ||
                                    fieldUpdates.some(s => s.type === "referralContact")
                                        ? {
                                              regionId,
                                          }
                                        : null,
                                clientContactParams:
                                    entity.type === AdvancedSearchEntityType.Client
                                        ? { relationship: entity.relationship }
                                        : null,
                                homeCareLeadContactParams:
                                    entity.type === AdvancedSearchEntityType.HomeCareLead
                                        ? { relationship: entity.relationship }
                                        : null,
                            },
                        });
                        dispatch({ type: "setContact", contact: created.createContact });
                    } catch (e) {
                        const message = e?.message || defaultErrorMessage;
                        dispatch({ type: "setError", message });
                    }
                }
            }
        },
        [
            state.status,
            state.contact,
            state.contactExists,
            state.pendingUpdates,
            entity,
            createContact,
            regionId,
            doPatch,
            dispatch,
        ],
    );

    useEffect(() => {
        if (state.status === "loaded" && state.contact && state.hasSaved) {
            if (state.pendingUpdates.length > 0) {
                doPatch(state.contact.id, state.pendingUpdates);
            } else {
                onSaved(state.contact);
            }
        }
    }, [state.contact]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleClose = React.useCallback(
        async (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
            e.preventDefault();
            dispatch({ type: "requestClose" });
        },
        [dispatch],
    );

    const handleReferenceContactChecked = React.useCallback(
        (checked: boolean) => {
            if (state.status === "loaded" && !state.contactExists) {
                if (checked) {
                    dispatch({ type: "ensureReferralContact", regionId });
                } else {
                    dispatch({ type: "clearReferralContacts" });
                }
            } else {
                handleFieldUpdates([
                    {
                        type: "referralContact",
                        patch: checked
                            ? {
                                  deleted: false,
                                  communityId:
                                      entity.type === AdvancedSearchEntityType.Community
                                          ? entity.communityId
                                          : undefined,
                                  summary: state.contact?.contactNotes || "",
                              }
                            : { deleted: true },
                    },
                ]);
            }
        },
        [
            state.status,
            state.contactExists,
            state.contact?.contactNotes,
            entity,
            handleFieldUpdates,
            regionId,
            dispatch,
        ],
    );

    useEffect(() => {
        if (
            state.closeRequested &&
            state.status === "loaded" &&
            state.pendingUpdates.length === 0
        ) {
            onClose();
        }

        if (state.closeRequested && state.status === "failed") {
            onClose();
        }
    }, [state.closeRequested, state.status, state.pendingUpdates]); // eslint-disable-line react-hooks/exhaustive-deps

    const policy = getEditorPolicy(entity);

    const matchingCommunityContact = matchingCommunityContactFor(state.contact, entity);
    const matchingClientContact = matchingClientContactFor(state.contact, entity);
    const matchingHomeCareLeadContact = matchingHomeCareLeadContactFor(
        state.contact,
        entity,
    );
    const matchingReferenceContact = state.contact?.referenceContacts.find(
        rc => rc.regionId === regionId,
    );

    if (state.status === "loading") {
        return <Spinner />;
    }

    return (
        <div className="space-y-2 md:space-y-4">
            {policy.allowReferralContactTracking ? (
                <AutosavingCheckbox
                    label="Track this contact as a referral contact"
                    initial={!!matchingReferenceContact}
                    onCommit={handleReferenceContactChecked}
                />
            ) : null}
            <Header iconName="08_ContactPerson">
                {state.contact?.name || "(no name)"}
            </Header>
            <div className="sm:flex sm:space-x-1 md:space-x-2 lg:space-x-4">
                <AutosavingInput
                    label="Name"
                    initial={state.contact?.name}
                    onCommit={newValue =>
                        handleFieldUpdates([
                            { type: "contact", patch: { name: newValue } },
                        ])
                    }
                />
                {policy.showRole ? (
                    <AutosavingInput
                        label="Role / title"
                        initial={state.contact?.role}
                        onCommit={newValue =>
                            handleFieldUpdates([
                                { type: "contact", patch: { role: newValue } },
                            ])
                        }
                    />
                ) : null}
                {policy.showRelationship ? (
                    <AutosavingInput
                        label="Relationship"
                        initial={state.contact?.relationship}
                        onCommit={newValue =>
                            handleFieldUpdates([
                                { type: "contact", patch: { relationship: newValue } },
                            ])
                        }
                    />
                ) : null}
                {policy.showProviderType ? (
                    <AutosavingInput
                        label="Type of provider"
                        initial={state.contact?.relationship}
                        onCommit={newValue =>
                            handleFieldUpdates([
                                { type: "contact", patch: { relationship: newValue } },
                            ])
                        }
                    />
                ) : null}
            </div>
            {policy.showBusinessName ? (
                <AutosavingInput
                    label="Clinic / business name (if applicable)"
                    initial={state.contact?.role}
                    onCommit={newValue =>
                        handleFieldUpdates([
                            { type: "contact", patch: { role: newValue } },
                        ])
                    }
                />
            ) : null}
            <div className="sm:flex sm:space-x-1 md:space-x-2 lg:space-x-4">
                {policy.showCellPhone ? (
                    <PhoneInputE164
                        label="Cell phone number"
                        initial={state.contact?.cellPhone}
                        onCommit={newValue =>
                            handleFieldUpdates([
                                { type: "contact", patch: { cellPhone: newValue } },
                            ])
                        }
                    />
                ) : null}
                <PhoneInputE164
                    label={policy.showCellPhone ? "Phone number (other)" : "Phone number"}
                    initial={state.contact?.phone1}
                    onCommit={newValue =>
                        handleFieldUpdates([
                            { type: "contact", patch: { phone1: newValue } },
                        ])
                    }
                />
                {policy.showFax ? (
                    <PhoneInputE164
                        label="Fax number"
                        initial={state.contact?.fax}
                        onCommit={newValue =>
                            handleFieldUpdates([
                                { type: "contact", patch: { fax: newValue } },
                            ])
                        }
                    />
                ) : null}
            </div>
            <div>
                <AutosavingInput
                    label="Email"
                    initial={state.contact?.email1}
                    onCommit={newValue =>
                        handleFieldUpdates([
                            { type: "contact", patch: { email1: newValue } },
                        ])
                    }
                />
            </div>
            <Setting name="emailOptOut">
                <div>
                    <AutosavingCheckbox
                        initial={!!state.contact?.email1OptOut}
                        label="Email Opt-out (for bulk/newsletter emails)"
                        onCommit={newValue =>
                            handleFieldUpdates([
                                { type: "contact", patch: { email1OptOut: newValue } },
                            ])
                        }
                    />
                </div>
            </Setting>
            {policy.showAddress ? (
                <LocaleContext.Consumer>
                    {locale => (
                        <div className="space-y-1 md:space-y-2">
                            <div>
                                <AutosavingInput
                                    label="Address"
                                    initial={state.contact?.address1}
                                    onCommit={newVal =>
                                        handleFieldUpdates([
                                            {
                                                type: "contact",
                                                patch: { address1: newVal },
                                            },
                                        ])
                                    }
                                />
                            </div>
                            <div className="sm:flex sm:space-x-1 md:space-x-2 lg:space-x-4">
                                <AutosavingInput
                                    label="City"
                                    initial={state.contact?.city}
                                    onCommit={newVal =>
                                        handleFieldUpdates([
                                            {
                                                type: "contact",
                                                patch: { city: newVal },
                                            },
                                        ])
                                    }
                                />
                                <AutosavingStateInput
                                    label={locale.strings.state}
                                    initial={state.contact?.state}
                                    onCommit={newVal =>
                                        handleFieldUpdates([
                                            {
                                                type: "contact",
                                                patch: { state: newVal },
                                            },
                                        ])
                                    }
                                />
                                <AutosavingInput
                                    label={locale.strings.zip}
                                    initial={state.contact?.zip}
                                    onCommit={newVal =>
                                        handleFieldUpdates([
                                            {
                                                type: "contact",
                                                patch: { zip: newVal },
                                            },
                                        ])
                                    }
                                />
                            </div>
                        </div>
                    )}
                </LocaleContext.Consumer>
            ) : null}
            {matchingReferenceContact ? (
                <div>
                    <AutosavingInput
                        label="Summary"
                        multiLine={true}
                        initial={matchingReferenceContact.summary}
                        onCommit={newVal =>
                            handleFieldUpdates([
                                {
                                    type: "referralContact",
                                    patch: { summary: newVal },
                                },
                                {
                                    type: "contact",
                                    patch: { contactNotes: newVal },
                                },
                            ])
                        }
                    />
                </div>
            ) : (
                <div>
                    <AutosavingInput
                        label={"Contact notes / details"}
                        multiLine={true}
                        initial={state.contact?.contactNotes}
                        onCommit={newVal =>
                            handleFieldUpdates([
                                { type: "contact", patch: { contactNotes: newVal } },
                            ])
                        }
                    />
                </div>
            )}
            {policy.showPrimaryContact &&
            entity.type === AdvancedSearchEntityType.Community ? (
                <div>
                    <AutosavingCheckbox
                        label="Primary contact"
                        initial={!!matchingCommunityContact?.primary}
                        onCommit={newValue =>
                            handleFieldUpdates([
                                {
                                    type: "communityContact",
                                    communityId: entity.communityId,
                                    patch: { primary: newValue },
                                },
                            ])
                        }
                    />
                </div>
            ) : null}
            {policy.showPowerOfAttorney &&
            entity.type === AdvancedSearchEntityType.Client ? (
                <PowerOfAttorneySelect
                    value={matchingClientContact?.powerOfAttorney || []}
                    onChange={newVal =>
                        handleFieldUpdates([
                            {
                                type: "clientContact",
                                clientId: entity.clientId,
                                relationship: entity.relationship,
                                patch: { powerOfAttorney: newVal },
                            },
                        ])
                    }
                />
            ) : null}
            {policy.showPowerOfAttorney &&
            entity.type === AdvancedSearchEntityType.HomeCareLead ? (
                <PowerOfAttorneySelect
                    value={matchingHomeCareLeadContact?.powerOfAttorney || []}
                    onChange={newVal =>
                        handleFieldUpdates([
                            {
                                type: "homeCareLeadContact",
                                homeCareLeadId: entity.homeCareLeadId,
                                relationship: entity.relationship,
                                patch: { powerOfAttorney: newVal },
                            },
                        ])
                    }
                />
            ) : null}
            <div className="flex justify-end items-center space-x-2">
                {state.closeRequested ? <Spinner /> : null}
                <PrimaryButton onClick={handleClose} disabled={state.closeRequested}>
                    Close
                </PrimaryButton>
            </div>
            {state.errorMessage ? (
                <InlineBanner type="error">
                    <p>The contact did not save for this reason: {state.errorMessage}</p>
                    <p>
                        Refresh your browser and try again. If this issue persists,
                        contact <SupportEmailLink />
                    </p>
                </InlineBanner>
            ) : null}
        </div>
    );
};
